import { useEffect, useRef, useState } from "react";
import { ReactComponent as SearchIcon } from "@ugg/shared/assets/svg/search-icon.svg";
import { FilterSelect } from "@ugg/shared/interfaces/filter-manager.interface";

// Free-text filter bound to the `search` query param — the champion search on
// the Classic / ARAM Mayhem tier lists and the augment search on the Mayhem
// augment surfaces.
//
// Unlike the dropdown filters this keeps a local copy of the value and writes
// to the URL on a debounce. Writing per keystroke would push a history entry
// and re-run validateQueryParams (and every consumer of it) on every character.
// The local copy is what keeps typing responsive; the URL is the source of
// truth everywhere else.
const WRITE_DELAY_MS = 300;

interface FilterSearchProps extends FilterSelect {
  placeholder: string;
  className?: string;
}

export default function FilterSearch(props: FilterSearchProps) {
  const { filters, onFilterChange, placeholder, className } = props;

  const urlValue = typeof filters.search === "string" ? filters.search : "";
  const [value, setValue] = useState(urlValue);

  // Tracks the last value this component put into (or read from) the URL, so
  // the two effects below can tell "the user is typing" apart from "the URL
  // changed underneath us".
  const settledValue = useRef(urlValue);

  // onFilterChange is rebuilt every render; holding it in a ref keeps it out of
  // the debounce effect's deps, so an unrelated re-render can't restart the timer.
  const onFilterChangeRef = useRef(onFilterChange);
  onFilterChangeRef.current = onFilterChange;

  // Adopt changes that came from outside this input: back/forward navigation,
  // or another filter rewriting the query string.
  useEffect(() => {
    if (urlValue !== settledValue.current) {
      settledValue.current = urlValue;
      setValue(urlValue);
    }
  }, [urlValue]);

  useEffect(() => {
    if (value === settledValue.current) return;
    const timer = setTimeout(() => {
      settledValue.current = value;
      // null clears the param entirely rather than leaving `?search=`.
      onFilterChangeRef.current("search", value ? { value, label: value } : null);
    }, WRITE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [value]);

  return (
    <div className={`relative flex items-center ${className || ""}`}>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        aria-label={placeholder}
        className="h-9 w-[220px] rounded-[6px] border border-[#25254b] bg-[#25254b] pl-[14px] pr-[34px] text-[14px] font-normal leading-5 text-[#cddcfe] placeholder:text-[#cddcfe] focus:border-[#3273fa] focus:outline-none"
      />
      <SearchIcon className="pointer-events-none absolute right-[12px] w-4 [&_path]:fill-[#cddcfe]" />
    </div>
  );
}
